import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import EvidenceCard from "../components/EvidenceCard";
import Marquee from "../components/Marquee";

const ACCENT = "#007AFF";

const FDA_IMG =
  "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const RETAIL_IMG =
  "https://images.unsplash.com/photo-1629959000608-3ec4fc3194b5?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const FINANCE_IMG =
  "https://images.unsplash.com/photo-1563013544-824ae1b704d3?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

export default function ImpactConsumers() {
  return (
    <div style={{ backgroundColor: "#050505", paddingTop: "64px" }}>
      {/* Header */}
      <div
        data-testid="consumers-header"
        style={{
          padding: "64px 24px 56px",
          borderBottom: "1px solid #1A1A1A",
          backgroundColor: "#0A0A0A",
        }}
      >
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          <p
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: "11px",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: ACCENT,
              marginBottom: "16px",
            }}
          >
            Page 05 — Evidence
          </p>
          <h1
            style={{
              fontFamily: "'Cabinet Grotesk', sans-serif",
              fontSize: "clamp(2rem, 5vw, 3.8rem)",
              fontWeight: 900,
              letterSpacing: "-0.03em",
              color: "#FFFFFF",
              marginBottom: "16px",
              lineHeight: 1.05,
              borderLeft: `4px solid ${ACCENT}`,
              paddingLeft: "24px",
            }}
          >
            Impact on Consumers
          </h1>
          <p
            style={{
              fontSize: "16px",
              color: "#71717A",
              maxWidth: "680px",
              lineHeight: 1.65,
              paddingLeft: "28px",
            }}
          >
            Consumer protection regulations shape what products are safe to
            buy, what prices are fair, and whether financial institutions can
            take advantage of the people they serve.
          </p>
        </div>
      </div>

      <Marquee />

      <div
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          padding: "64px 24px",
          display: "grid",
          gridTemplateColumns: "280px 1fr",
          gap: "64px",
          alignItems: "start",
        }}
        className="impact-layout"
      >
        {/* Sticky Commentary */}
        <aside
          data-testid="consumers-commentary"
          style={{ position: "sticky", top: "88px" }}
        >
          <p
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: "10px",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: "#52525B",
              marginBottom: "16px",
            }}
          >
            Student Commentary
          </p>
          <p
            style={{
              color: "#D1D1D6",
              fontSize: "14px",
              lineHeight: 1.8,
              marginBottom: "20px",
            }}
          >
            Consumer protection was the category that shifted my overall
            view of regulation the most. When I read about the thalidomide
            case — a drug that was blocked in the U.S. by a single FDA
            scientist while it caused thousands of birth defects in Europe —
            it really put into perspective what "no regulation" could mean.
          </p>
          <p
            style={{
              color: "#D1D1D6",
              fontSize: "14px",
              lineHeight: 1.8,
              marginBottom: "20px",
            }}
          >
            The CFPB data showing $17.5 billion returned to consumers was
            also striking. That's real money taken from real people through
            illegal practices — and a government agency got it back.
          </p>
          <p
            style={{
              color: "#D1D1D6",
              fontSize: "14px",
              lineHeight: 1.8,
            }}
          >
            At the same time, I think the FDA drug approval example shows
            that even well-designed regulation involves trade-offs. Slow
            approval processes save lives but also delay access to medicines
            that could save different lives. There's rarely a perfect answer.
          </p>

          <div style={{ marginTop: "36px", padding: "20px", backgroundColor: "#0D0D0D", border: "1px solid #1A1A1A" }}>
            <p
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: "10px",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "#52525B",
                marginBottom: "12px",
              }}
            >
              Research Question
            </p>
            <p style={{ color: "#A1A1AA", fontSize: "13px", lineHeight: 1.7, fontStyle: "italic" }}>
              "How does government regulation affect businesses, workers, and
              consumers in the U.S. economy?"
            </p>
          </div>
        </aside>

        {/* Evidence Cards */}
        <div data-testid="consumers-evidence">
          <EvidenceCard
            artifactNum={1}
            title="FDA Drug Approval: Protecting Consumers or Blocking Access?"
            imageUrl={FDA_IMG}
            imageAlt="Pharmaceutical pills and medical products regulated by the FDA"
            accentColor={ACCENT}
            caption="The FDA's rigorous drug approval process has prevented many dangerous medications from reaching American consumers — but critics argue it also delays access to potentially life-saving treatments."
            citation="[7]"
          >
            <p style={{ marginBottom: "14px" }}>
              The Food and Drug Administration's drug approval process is one
              of the most consequential regulatory systems in the world. Before
              any medication can be sold in the U.S., it must undergo clinical
              trials typically spanning{" "}
              <strong style={{ color: "#FFFFFF" }}>10–12 years</strong> and
              costing over{" "}
              <strong style={{ color: "#FFFFFF" }}>$1 billion</strong> per drug.
              <sup style={{ color: ACCENT }}>[7]</sup>
            </p>
            <p style={{ marginBottom: "14px" }}>
              The most famous example of why this process matters is
              thalidomide. In the late 1950s and early 1960s, this drug was
              widely marketed in Europe as a sleep aid and morning sickness
              treatment. It caused severe birth defects in thousands of
              children. It was blocked from U.S. approval by FDA scientist Dr.
              Frances Kelsey, who demanded more safety data despite industry
              pressure — saving an untold number of American families from
              tragedy.
            </p>
            <p>
              However, the same strict standards have a downside: drugs that
              could help patients are often unavailable in the U.S. for years
              after they've been approved in Europe or Canada. The FDA's
              "accelerated approval" and "breakthrough therapy" designations
              were created to address this tension — showing that even
              regulatory agencies can evolve when the trade-offs become clear.
            </p>
          </EvidenceCard>

          <EvidenceCard
            artifactNum={2}
            title="Antitrust Enforcement: Breaking Up Monopolies for Consumer Benefit"
            imageUrl={RETAIL_IMG}
            imageAlt="Consumer retail shopping environment"
            accentColor={ACCENT}
            caption="Antitrust enforcement by the FTC and DOJ has historically protected consumers by preventing monopolies from forming — with the AT&T breakup as a landmark example."
            citation="[8]"
          >
            <p style={{ marginBottom: "14px" }}>
              Antitrust laws, enforced by both the Federal Trade Commission
              (FTC) and the Department of Justice (DOJ), prevent monopolies
              and price-fixing that would harm consumers. The most famous
              example in U.S. history is the breakup of{" "}
              <strong style={{ color: "#FFFFFF" }}>AT&T in 1984</strong>. When
              the DOJ successfully sued to break up the telephone giant, the
              resulting competition drove down phone service costs for millions
              of Americans and sparked rapid innovation in
              telecommunications.
              <sup style={{ color: ACCENT }}>[8]</sup>
            </p>
            <p style={{ marginBottom: "14px" }}>
              Today, antitrust enforcement has a new focus: the tech industry.
              The FTC has filed lawsuits against Google, Amazon, and Meta,
              arguing they have used their market dominance to crush
              competition and harm consumers. Google, for example, pays{" "}
              <strong style={{ color: "#FFFFFF" }}>$10+ billion annually</strong>{" "}
              to be the default search engine on Apple devices — a practice the
              DOJ argues illegally maintains its search monopoly.
            </p>
            <p>
              When antitrust laws are effectively enforced, consumers typically
              benefit from lower prices, more choices, and greater innovation.
              The challenge is that modern tech monopolies are very different
              from the industrial monopolies of the 20th century — which makes
              the legal questions far more complex.
            </p>
          </EvidenceCard>

          <EvidenceCard
            artifactNum={3}
            title="The CFPB: $17.5 Billion Returned to Consumers"
            imageUrl={FINANCE_IMG}
            imageAlt="Financial protection and consumer banking"
            accentColor={ACCENT}
            caption="The Consumer Financial Protection Bureau was created specifically to protect everyday consumers from illegal financial practices — and its track record of returning billions of dollars demonstrates the real-world impact of consumer protection regulation."
            citation="[9]"
          >
            <p style={{ marginBottom: "14px" }}>
              Created by the Dodd-Frank Act in 2010, the Consumer Financial
              Protection Bureau (CFPB) was specifically designed to protect
              consumers from unfair, deceptive, or abusive financial practices.
              In its first decade of operation, the CFPB returned over{" "}
              <strong style={{ color: "#FFFFFF" }}>$17.5 billion</strong> to
              consumers who had been victimized by illegal practices at banks,
              credit card companies, mortgage servicers, and other financial
              firms.
              <sup style={{ color: ACCENT }}>[9]</sup>
            </p>
            <p style={{ marginBottom: "14px" }}>
              One of the CFPB's most famous actions was its{" "}
              <strong style={{ color: "#FFFFFF" }}>$185 million fine</strong>{" "}
              against Wells Fargo in 2016, after the bank's employees secretly
              opened millions of unauthorized accounts in customers' names to
              meet unrealistic sales targets. Without the CFPB, this fraud
              might have continued indefinitely.
            </p>
            <p>
              However, the CFPB has been highly politically controversial.
              Financial industry groups argue that its broad authority
              overregulates the industry, raising costs and reducing credit
              availability. The agency has been subject to multiple legal
              challenges and political efforts to weaken or eliminate it —
              illustrating how consumer protection regulations are often at the
              center of broader debates about the role of government in the
              economy.
            </p>
          </EvidenceCard>

          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginTop: "16px" }}>
            <Link
              to="/citations"
              data-testid="next-page-citations"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: "#007AFF",
                color: "#FFFFFF",
                padding: "12px 24px",
                fontSize: "13px",
                fontWeight: 700,
                textDecoration: "none",
              }}
            >
              Next: MLA Citations <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .impact-layout {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
